import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { IconDatabase, IconPointFilled } from '@rossum/ui/icons/tabler';
import { Box, Stack, SvgIcon, Typography } from '@rossum/ui/material';
import type { ComputedFieldDraft } from './types';

type FieldWelcomeScreenProps = {
  draft: ComputedFieldDraft;
};

/**
 * Shown instead of the general welcome screen when the drawer was opened on a computed field. The
 * general tips (attachments, diagrams, slash commands) describe a conversation this one is not:
 * the user arrived from a single button on a single field and wants to talk about that field.
 */
export const FieldWelcomeScreen = ({ draft }: FieldWelcomeScreenProps) => {
  const intl = useIntl();

  // Reading the stored shape rather than a parsed one: this is orientation text, so a
  // configuration we cannot read should cost the query count, not the whole screen.
  const queries = draft.configuration?.['queries'];
  const queryCount = Array.isArray(queries) ? queries.length : 0;

  const bold = (chunks: ReactNode) => <strong>{chunks}</strong>;

  const options = [
    intl.formatMessage({ id: 'features.chat.fieldWelcome.option.change' }),
    intl.formatMessage({ id: 'features.chat.fieldWelcome.option.explain' }),
    intl.formatMessage({ id: 'features.chat.fieldWelcome.option.test' }),
  ];

  return (
    <Stack sx={{ flex: 1, minHeight: 0 }}>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 4,
        }}
      >
        <Stack alignItems="center" spacing={2} sx={{ maxWidth: 420 }}>
          <SvgIcon fontSize="xLarge" sx={{ color: 'aurora.main' }}>
            <IconDatabase />
          </SvgIcon>

          <Typography
            variant="h5"
            fontWeight={600}
            textAlign="center"
            data-cy="chat-field-welcome-heading"
          >
            {intl.formatMessage(
              { id: 'features.chat.fieldWelcome.title' },
              { field: draft.label }
            )}
          </Typography>

          {queryCount > 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              {intl.formatMessage(
                { id: 'features.chat.fieldWelcome.configured' },
                { count: queryCount, bold }
              )}
            </Typography>
          )}

          <Typography variant="body2" fontWeight={500} sx={{ pt: 1 }}>
            {intl.formatMessage({ id: 'features.chat.fieldWelcome.prompt' })}
          </Typography>

          <Stack
            component="ul"
            spacing={1}
            sx={{ m: 0, p: 0, listStyle: 'none' }}
          >
            {options.map(option => (
              <Stack
                key={option}
                component="li"
                direction="row"
                spacing={1.5}
                alignItems="flex-start"
              >
                <SvgIcon
                  fontSize="small"
                  sx={{ color: 'aurora.main', flexShrink: 0 }}
                >
                  <IconPointFilled />
                </SvgIcon>
                <Typography variant="body2" color="text.secondary">
                  {option}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
};
